// Risk-adjusted performance table — Sharpe, Sortino, Calmar.
// Calmar = annualised return / max drawdown — we compute it client-side from totalReturn
// and maxDD since the backend doesn't emit it directly.

import type { BacktestResponseDto } from "../../api/types";
import { selectPrimary } from "./primary";

interface Props {
  result: BacktestResponseDto;
}

export function RiskAdjustedTable({ result }: Props) {
  const primary = selectPrimary(result);
  if (!primary) {
    return (
      <div style={{ padding: 12, background: "#161b22", borderRadius: 4, fontSize: 12, color: "#8b949e" }}>
        No risk-adjusted data.
      </div>
    );
  }
  const totalReturn = primary.totalReturn;
  const maxDD = "maxDD" in primary ? primary.maxDD : primary.maxStratDD;
  const calmar = Math.abs(maxDD) > 0 ? totalReturn / Math.abs(maxDD) : 0;
  return (
    <div style={{ background: "#0d1117", border: "1px solid #30363d", borderRadius: 6, padding: 12 }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Risk-adjusted performance</div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <tbody>
          <Row label="Sharpe ratio (per-trade)"  value={fmt(primary.sharpe)} />
          <Row label="Sortino ratio (per-trade)" value={fmt(primary.sortino)} />
          <Row label="Calmar ratio"              value={fmt(calmar)} />
        </tbody>
      </table>
      <div style={{ fontSize: 10, color: "#8b949e", marginTop: 6 }}>
        Per-trade Sharpe/Sortino — not annualised. Calmar = total return / |max drawdown|.
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <tr style={{ borderTop: "1px solid #21262d" }}>
      <td style={{ padding: "8px 8px", color: "#8b949e", fontSize: 12 }}>{label}</td>
      <td style={{ padding: "8px 8px", textAlign: "right", color: "#c9d1d9", fontFamily: "monospace", fontSize: 12 }}>
        {value}
      </td>
    </tr>
  );
}

function fmt(n: number): string {
  if (!Number.isFinite(n)) return "—";
  if (n === 99) return "∞";
  return n.toFixed(3);
}
